import { Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

/**
 * ProtectedRoute - Wrapper component for permission-based route protection
 * 
 * Usage:
 * <Route path="/admin/users" element={
 *   <ProtectedRoute permission="users">
 *     <Users />
 *   </ProtectedRoute>
 * } />
 */
export default function ProtectedRoute({ children, permission }) {
  const { user, hasPermission, isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />
  }

  // If no permission required, allow access
  if (!permission) {
    return children
  }

  // Check if user has the required permission
  if (!hasPermission(permission)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96 text-center p-8">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600 mb-6">
          You don't have permission to access this page.
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Your role: <span className="font-medium capitalize">{user?.role}</span>
        </p>
        <a 
          href="/admin" 
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go to Dashboard
        </a>
      </div>
    )
  }

  return children
}
